import { create } from 'zustand';
import { format, startOfMonth, startOfYear, subDays, eachDayOfInterval, parseISO, startOfWeek, endOfWeek, getWeek, getYear, addDays, getDay, subWeeks, nextSunday, isSunday, addWeeks } from 'date-fns';
import supabase from '../lib/supabase';

const useDashboardStore = create((set, get) => ({
  // Filter State - CHANGED: Default to DATE preset instead of MTD
  preset: 'DATE',
  dateFrom: format(new Date(), 'yyyy-MM-dd'), // Will be updated on init
  dateTo: format(new Date(), 'yyyy-MM-dd'), // Will be updated on init
  compare: 'NONE',
  compareAlign: null,
  departments: [],
  categories: [],
  products: [],

  // UI State
  primaryMetric: 'revenue',
  topBottomMode: 'TOP',
  topBottomMetric: 'revenue',
  miniMultipleGroup: 'department',
  drillDrawerOpen: false,
  monochrome: false,
  uploadModalOpen: false,
  logoUploadModalOpen: false,
  dataManagementOpen: false,
  clearModalOpen: false,

  // Data
  kpis: null,
  seriesData: null,
  rankedData: null,
  miniMultipleData: null,
  drillData: null,
  loading: false,
  error: null,
  hasData: false,

  // Actions
  setPreset: async (preset) => {
    const state = get();
    let dateFrom, dateTo;
    const today = new Date();

    switch (preset) {
      case 'DATE':
        // For DATE preset, automatically jump to the latest date with data
        try {
          console.log('📅 DATE preset selected - finding latest date with data...');
          
          // Test connection first
          const isConnected = await get().testConnection();
          if (!isConnected) {
            console.warn('Connection failed, using today as fallback');
            dateFrom = dateTo = format(today, 'yyyy-MM-dd');
            break;
          }

          // Get the actual latest date with sales data
          const { data, error } = await supabase
            .from('sales_data_br7x9m4k2q')
            .select('txn_date')
            .gt('net_sales', 0) // Only dates with actual sales
            .order('txn_date', { ascending: false })
            .limit(1);

          if (error) {
            throw error;
          }

          if (data && data.length > 0) {
            const latestDate = data[0].txn_date;
            console.log(`📅 Found latest date with data: ${latestDate}`);
            dateFrom = dateTo = latestDate;
          } else {
            console.log('📅 No data found, using today as fallback');
            dateFrom = dateTo = format(today, 'yyyy-MM-dd');
          }
        } catch (error) {
          console.error('Error finding latest date:', error);
          dateFrom = dateTo = format(today, 'yyyy-MM-dd');
        }
        break;

      case 'WEEK':
        // For WEEK preset, get current week (Monday to Sunday) but EXCLUDE current date
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday start
        const yesterday = subDays(today, 1); // EXCLUDE current date
        const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 }); // Sunday end
        
        // If yesterday is before the week start, use the full previous week
        if (yesterday < currentWeekStart) {
          const previousWeekStart = subWeeks(currentWeekStart, 1);
          const previousWeekEnd = subDays(currentWeekStart, 1);
          dateFrom = format(previousWeekStart, 'yyyy-MM-dd');
          dateTo = format(previousWeekEnd, 'yyyy-MM-dd');
          console.log(`📅 WEEK preset: Using previous week (current week has no historical data)`);
        } else {
          dateFrom = format(currentWeekStart, 'yyyy-MM-dd');
          dateTo = format(yesterday, 'yyyy-MM-dd'); // EXCLUDE current date
          console.log(`📅 WEEK preset: Current week ${getWeek(today, { weekStartsOn: 1 })} of ${getYear(today)} (excluding today)`);
        }
        
        console.log(`📅 Week range: ${dateFrom} to ${dateTo} (excluding current date)`);
        break;

      case 'MTD':
        // For MTD preset, EXCLUDE current date from the range
        const monthStart = startOfMonth(today);
        const yesterdayMTD = subDays(today, 1); // EXCLUDE current date
        
        // If yesterday is before the month start, use the previous month
        if (yesterdayMTD < monthStart) {
          const previousMonth = subDays(monthStart, 1);
          const previousMonthStart = startOfMonth(previousMonth);
          dateFrom = format(previousMonthStart, 'yyyy-MM-dd');
          dateTo = format(previousMonth, 'yyyy-MM-dd');
          console.log(`📅 MTD preset: Using previous month (current month has no historical data)`);
        } else {
          dateFrom = format(monthStart, 'yyyy-MM-dd');
          dateTo = format(yesterdayMTD, 'yyyy-MM-dd'); // EXCLUDE current date
          console.log(`📅 MTD preset: Month to date (excluding today)`);
        }
        
        console.log(`📅 MTD range: ${dateFrom} to ${dateTo} (excluding current date)`);
        break;

      case 'YTD':
        dateFrom = format(startOfYear(today), 'yyyy-MM-dd');
        dateTo = format(today, 'yyyy-MM-dd');
        break;

      default:
        dateFrom = state.dateFrom;
        dateTo = state.dateTo;
    }

    set({ preset, dateFrom, dateTo });
    get().loadData();
  },

  // FIXED: Don't automatically change preset to CUSTOM
  setDateRange: (dateFrom, dateTo, preservePreset = false) => {
    const currentState = get();
    
    // If preservePreset is true, keep the current preset
    // This is used when user changes date within Date preset
    if (preservePreset) {
      set({ dateFrom, dateTo });
    } else {
      // Only change to CUSTOM if not preserving preset
      set({ dateFrom, dateTo, preset: 'CUSTOM' });
    }
    
    get().loadData();
  },

  setCompare: (compare, compareAlign = null) => {
    set({ compare, compareAlign });
    get().loadData();
  },

  setFilters: (filters) => {
    set(filters);
    get().loadData();
  },

  setPrimaryMetric: (metric) => set({ primaryMetric: metric }),

  setTopBottomConfig: (config) => {
    const currentState = get();
    const newState = {
      topBottomMode: config.mode || currentState.topBottomMode,
      topBottomMetric: config.metric || currentState.topBottomMetric,
    };
    set(newState);
    
    // Always reload ranked data when any top/bottom config changes
    if (config.mode || config.metric) {
      get().loadRankedData();
    }
  },

  setMiniMultipleGroup: (group) => {
    set({ miniMultipleGroup: group });
    // Reload mini multiples data when group changes
    if (get().hasData) {
      get().loadMiniMultipleData();
    }
  },

  toggleDrillDrawer: () => set({ drillDrawerOpen: !get().drillDrawerOpen }),
  toggleMonochrome: () => set({ monochrome: !get().monochrome }),
  toggleUploadModal: () => set({ uploadModalOpen: !get().uploadModalOpen }),
  toggleLogoUploadModal: () => set({ logoUploadModalOpen: !get().logoUploadModalOpen }),
  toggleDataManagement: () => set({ dataManagementOpen: !get().dataManagementOpen }),
  toggleClearModal: () => set({ clearModalOpen: !get().clearModalOpen }),

  // Test Supabase connection
  testConnection: async () => {
    try {
      const { data, error } = await supabase
        .from('sales_data_br7x9m4k2q')
        .select('count')
        .limit(1);

      if (error) {
        throw error;
      }

      console.log('✅ Supabase connection successful');
      return true;
    } catch (error) {
      console.error('❌ Supabase connection failed:', error);
      return false;
    }
  },

  // CRITICAL: Fetch ALL data using pagination to overcome 1000 row limit
  fetchAllData: async (baseQuery) => {
    const BATCH_SIZE = 1000;
    let allData = [];
    let start = 0;
    let hasMoreData = true;

    console.log('🔄 Starting paginated data fetch to get ALL records...');

    while (hasMoreData) {
      console.log(`📦 Fetching batch starting at row ${start}...`);

      // Clone the query and add pagination
      const batchQuery = baseQuery
        .range(start, start + BATCH_SIZE - 1);

      const { data: batchData, error } = await batchQuery;

      if (error) {
        throw error;
      }

      if (!batchData || batchData.length === 0) {
        hasMoreData = false;
        break;
      }

      allData.push(...batchData);
      console.log(`📦 Fetched ${batchData.length} records (total so far: ${allData.length})`);

      // If we got fewer records than the batch size, we've reached the end
      if (batchData.length < BATCH_SIZE) {
        hasMoreData = false;
      } else {
        start += BATCH_SIZE;
      }

      // Small delay to prevent overwhelming the API
      if (hasMoreData) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    console.log(`✅ Pagination complete! Fetched ${allData.length} total records`);
    return allData;
  },

  // NEW: Initialize dashboard with latest date
  initializeWithLatestDate: async () => {
    try {
      console.log('🚀 Initializing dashboard with latest date...');
      set({ loading: true, error: null });

      // Test connection first
      const isConnected = await get().testConnection();
      if (!isConnected) {
        console.warn('⚠️ Database connection failed during initialization');
        // Set to today's date as fallback and continue
        const today = format(new Date(), 'yyyy-MM-dd');
        set({ preset: 'DATE', dateFrom: today, dateTo: today, loading: false });
        return;
      }

      // Get the actual latest date with sales data
      const { data, error } = await supabase
        .from('sales_data_br7x9m4k2q')
        .select('txn_date')
        .gt('net_sales', 0) // Only dates with actual sales
        .order('txn_date', { ascending: false })
        .limit(1);

      if (error) {
        throw error;
      }

      let latestDate;
      if (data && data.length > 0) {
        latestDate = data[0].txn_date;
        console.log(`📅 Found latest date with data: ${latestDate}`);
      } else {
        // No data in database, use today as fallback
        latestDate = format(new Date(), 'yyyy-MM-dd');
        console.log('📅 No data found in database, using today as fallback');
      }

      // Set the DATE preset with the latest date
      set({ preset: 'DATE', dateFrom: latestDate, dateTo: latestDate });
      console.log('✅ Dashboard initialized with DATE preset and latest data date');

      // Load data for the latest date
      await get().loadData();
    } catch (error) {
      console.error('❌ Error during dashboard initialization:', error);
      // Fallback to today's date
      const today = format(new Date(), 'yyyy-MM-dd');
      set({ preset: 'DATE', dateFrom: today, dateTo: today, error: `Initialization warning: ${error.message}`, loading: false });
    }
  },

  // IMPROVED: Jump to latest date with actual data
  jumpToLatest: async () => {
    try {
      // Test connection first
      const isConnected = await get().testConnection();
      if (!isConnected) {
        throw new Error('Database connection failed. Please check your internet connection.');
      }

      // Get the actual latest date with sales data
      const { data, error } = await supabase
        .from('sales_data_br7x9m4k2q')
        .select('txn_date')
        .gt('net_sales', 0) // Only dates with actual sales
        .order('txn_date', { ascending: false })
        .limit(1);

      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        const latestDate = data[0].txn_date;
        console.log(`📅 Jumping to latest date with data: ${latestDate}`);
        set({ dateFrom: latestDate, dateTo: latestDate, preset: 'DATE' });
        get().loadData();
      } else {
        // Fallback to today if no data found
        const today = new Date().toISOString().split('T')[0];
        set({ dateFrom: today, dateTo: today, preset: 'DATE' });
        get().loadData();
      }
    } catch (error) {
      console.error('Error jumping to latest date:', error);
      set({ error: `Failed to jump to latest date: ${error.message}` });
    }
  },

  // Clear database function
  clearDatabase: async () => {
    set({ loading: true, error: null });

    try {
      // Test connection first
      console.log('🔍 Testing database connection...');
      const isConnected = await get().testConnection();
      if (!isConnected) {
        throw new Error('Cannot connect to database. Please check your internet connection and try again.');
      }

      console.log('🗑️ Starting database clear operation...');

      // Method 1: Try bulk delete first (most efficient)
      try {
        const { error: bulkDeleteError } = await supabase
          .from('sales_data_br7x9m4k2q')
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records

        if (bulkDeleteError) {
          console.warn('Bulk delete failed, trying alternative method...', bulkDeleteError);
          throw bulkDeleteError;
        }

        console.log('✅ Bulk delete successful');
      } catch (bulkError) {
        console.log('🔄 Bulk delete failed, trying batch method...');

        // Method 2: Fetch and delete in batches (fallback)
        let hasMoreRecords = true;
        let deletedCount = 0;
        const BATCH_SIZE = 100;

        while (hasMoreRecords) {
          // Fetch a batch of records
          const { data: records, error: fetchError } = await supabase
            .from('sales_data_br7x9m4k2q')
            .select('id')
            .limit(BATCH_SIZE);

          if (fetchError) {
            throw new Error(`Failed to fetch records: ${fetchError.message}`);
          }

          if (!records || records.length === 0) {
            hasMoreRecords = false;
            break;
          }

          // Delete the batch
          const recordIds = records.map(record => record.id);
          const { error: deleteError } = await supabase
            .from('sales_data_br7x9m4k2q')
            .delete()
            .in('id', recordIds);

          if (deleteError) {
            throw new Error(`Failed to delete batch: ${deleteError.message}`);
          }

          deletedCount += records.length;
          console.log(`🗑️ Deleted ${deletedCount} records so far...`);

          // If we got fewer records than the batch size, we're done
          if (records.length < BATCH_SIZE) {
            hasMoreRecords = false;
          }

          // Small delay to prevent overwhelming the database
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        console.log(`✅ Batch delete completed. Total deleted: ${deletedCount}`);
      }

      // Reset the store state
      set({
        kpis: get().getEmptyKPIs(),
        seriesData: [],
        rankedData: [],
        miniMultipleData: null,
        hasData: false,
        loading: false,
        clearModalOpen: false,
        dataManagementOpen: false,
        error: null
      });

      console.log('✅ Database cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing database:', error);

      let errorMessage = 'Failed to clear database';
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error.message.includes('JWT') || error.message.includes('unauthorized')) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. The database might be busy, please try again.';
      } else {
        errorMessage = `Failed to clear database: ${error.message}`;
      }

      set({ error: errorMessage, loading: false });
    }
  },

  // FIXED: Main data loading function with pagination and trend calculation
  loadData: async () => {
    set({ loading: true, error: null });

    try {
      const state = get();
      const { dateFrom, dateTo, departments, categories, products, compare, preset } = state;

      // Test connection first
      const isConnected = await get().testConnection();
      if (!isConnected) {
        throw new Error('Database connection failed. Please check your internet connection.');
      }

      console.log('📊 Loading ALL data from database with pagination...');
      console.log(`📅 Date range: ${dateFrom} to ${dateTo}`);
      console.log('🔍 Filters:', { departments, categories, products });

      // Build the base query WITHOUT executing it
      let baseQuery = supabase
        .from('sales_data_br7x9m4k2q')
        .select('*')
        .gte('txn_date', dateFrom)
        .lte('txn_date', dateTo)
        .order('txn_date', { ascending: true });

      // Apply filters to base query
      if (departments.length > 0) {
        baseQuery = baseQuery.in('department', departments);
      }
      if (categories.length > 0) {
        baseQuery = baseQuery.in('category', categories);
      }
      if (products.length > 0) {
        baseQuery = baseQuery.in('product_name', products);
      }

      // Fetch ALL data using pagination
      const salesData = await get().fetchAllData(baseQuery);

      if (!salesData || salesData.length === 0) {
        console.log('📊 No data found for current filters');
        set({
          kpis: get().getEmptyKPIs(),
          seriesData: [],
          rankedData: [],
          miniMultipleData: null,
          hasData: false,
          loading: false
        });
        return;
      }

      console.log(`📊 Processing ALL ${salesData.length} records (not limited to 1000)...`);

      // Get comparison data - Enhanced for DATE preset, WEEK preset, and DoW vs DoW
      let comparisonData = null;
      if (compare !== 'NONE') {
        console.log(`📊 Loading comparison data for ${compare}...`);
        comparisonData = await get().getComparisonData(dateFrom, dateTo, compare, departments, categories, products);
      } else if (preset === 'DATE' && dateFrom === dateTo) {
        // Auto-enable 7-day trend analysis for DATE preset
        console.log(`📊 DATE preset detected - enabling automatic 7-day trend analysis...`);
        comparisonData = await get().getDatePresetComparisonData(dateFrom, departments, categories, products);
      }

      // Process the data with FIXED calculations and trend comparison
      const processedData = await get().processRealDataFixed(salesData, dateFrom, dateTo, comparisonData);

      set({
        ...processedData,
        hasData: true,
        loading: false
      });

      console.log('✅ Data loading completed successfully with ALL records');
    } catch (error) {
      console.error('Error loading data:', error);

      let errorMessage = 'Failed to load dashboard data';
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error.message.includes('JWT') || error.message.includes('unauthorized')) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      } else {
        errorMessage = error.message;
      }

      set({
        error: errorMessage,
        kpis: get().getEmptyKPIs(),
        seriesData: [],
        rankedData: [],
        miniMultipleData: null,
        hasData: false,
        loading: false
      });
    }
  },

  // ENHANCED: Get comparison data for trend calculations including 12-week WoW with structured return and CUSTOM preset support
  getComparisonData: async (dateFrom, dateTo, compareType, departments, categories, products) => {
    try {
      let comparisonDateFrom, comparisonDateTo;
      const startDate = new Date(dateFrom);
      const endDate = new Date(dateTo);

      switch (compareType) {
        case 'DOW':
          // DoW vs DoW - Only available for single date selection
          if (dateFrom !== dateTo) {
            console.log('📊 DoW vs DoW is only available for single date selection');
            return null;
          }

          console.log('📊 DoW vs DoW: Finding 30 similar days of the week...');
          const selectedDate = new Date(dateFrom);
          const dayOfWeek = getDay(selectedDate); // 0=Sunday, 1=Monday, etc.
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

          console.log(`📅 Selected date: ${dateFrom} (${dayNames[dayOfWeek]})`);
          console.log(`📅 Looking for 30 previous ${dayNames[dayOfWeek]}s...`);

          // Get all dates with the same day of week going backwards
          const similarDates = [];
          let currentDate = new Date(selectedDate);
          currentDate.setDate(currentDate.getDate() - 7); // Start from 1 week before

          // Find up to 30 similar days of the week going backwards
          while (similarDates.length < 30) {
            similarDates.push(format(currentDate, 'yyyy-MM-dd'));
            currentDate.setDate(currentDate.getDate() - 7); // Go back another week

            // Safety check to prevent infinite loop (don't go back more than 2 years)
            if (currentDate < new Date(selectedDate.getTime() - (2 * 365 * 24 * 60 * 60 * 1000))) {
              break;
            }
          }

          console.log(`📅 Found ${similarDates.length} ${dayNames[dayOfWeek]} dates to compare against:`);
          console.log(`📅 Date range: ${similarDates[similarDates.length - 1]} to ${similarDates[0]}`);

          // Query for data on all these similar days
          let dowQuery = supabase
            .from('sales_data_br7x9m4k2q')
            .select('*')
            .in('txn_date', similarDates)
            .order('txn_date', { ascending: true });

          // Apply same filters
          if (departments.length > 0) {
            dowQuery = dowQuery.in('department', departments);
          }
          if (categories.length > 0) {
            dowQuery = dowQuery.in('category', categories);
          }
          if (products.length > 0) {
            dowQuery = dowQuery.in('product_name', products);
          }

          const dowData = await get().fetchAllData(dowQuery);
          console.log(`📊 DoW vs DoW: Fetched ${dowData?.length || 0} records from ${similarDates.length} ${dayNames[dayOfWeek]}s`);
          return dowData;

        case 'WOW':
          // ENHANCED: Week over Week with support for WEEK and CUSTOM presets
          const currentPreset = get().preset;

          // Handle WEEK preset (Monday to Sunday weeks)
          if (currentPreset === 'WEEK' && dateFrom !== dateTo) {
            console.log('📊 WoW for WEEK preset: Finding 12 previous weeks...');

            // Calculate week boundaries for current week and 12 previous weeks
            const currentWeekStart = startOfWeek(startDate, { weekStartsOn: 1 });
            const weeks = [];

            // Generate 12 previous weeks
            for (let i = 1; i <= 12; i++) {
              const weekStart = subWeeks(currentWeekStart, i);
              const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
              weeks.push({
                start: format(weekStart, 'yyyy-MM-dd'),
                end: format(weekEnd, 'yyyy-MM-dd'),
                weekNumber: getWeek(weekStart, { weekStartsOn: 1 }),
                year: getYear(weekStart),
                weekIndex: i // For ordering (1 = most recent, 12 = oldest)
              });
            }

            console.log(`📅 Fetching data for 12 previous weeks:`);
            weeks.forEach((week, i) => {
              console.log(`  Week ${i + 1}: ${week.start} to ${week.end} (Week ${week.weekNumber} ${week.year})`);
            });

            // Fetch data for all 12 weeks with week metadata
            const allWeeksData = [];
            for (const week of weeks) {
              let weekQuery = supabase
                .from('sales_data_br7x9m4k2q')
                .select('*')
                .gte('txn_date', week.start)
                .lte('txn_date', week.end)
                .order('txn_date', { ascending: true });

              // Apply same filters
              if (departments.length > 0) {
                weekQuery = weekQuery.in('department', departments);
              }
              if (categories.length > 0) {
                weekQuery = weekQuery.in('category', categories);
              }
              if (products.length > 0) {
                weekQuery = weekQuery.in('product_name', products);
              }

              const weekData = await get().fetchAllData(weekQuery);

              // Add week metadata to each record
              const weekDataWithMeta = weekData.map(record => ({
                ...record,
                weekStart: week.start,
                weekEnd: week.end,
                weekNumber: week.weekNumber,
                weekYear: week.year,
                weekIndex: week.weekIndex,
                weekLabel: `Week ${week.weekNumber} ${week.year}`
              }));

              allWeeksData.push(...weekDataWithMeta);
            }

            console.log(`📊 WoW (12 weeks): Fetched ${allWeeksData.length} records from 12 previous weeks`);

            // Return structured data with week information
            return {
              data: allWeeksData,
              type: '12_week_wow',
              weeks: weeks
            };
          }
          // NEW: Handle CUSTOM preset with Sunday-based weeks
          else if (currentPreset === 'CUSTOM') {
            console.log('📊 WoW for CUSTOM preset: Finding nearest Sunday and 12 previous weeks...');

            // Find the nearest Sunday after the end date
            let referenceSunday;
            if (isSunday(endDate)) {
              referenceSunday = endDate;
              console.log(`📅 End date ${dateTo} is already a Sunday`);
            } else {
              referenceSunday = nextSunday(endDate);
              console.log(`📅 End date ${dateTo} → nearest Sunday: ${format(referenceSunday, 'yyyy-MM-dd')}`);
            }

            // Calculate the week that contains the current date range
            const currentWeekStart = addDays(referenceSunday, -6); // Monday before the reference Sunday
            const currentWeekEnd = referenceSunday; // The reference Sunday
            const currentWeekNumber = getWeek(referenceSunday, { weekStartsOn: 1 });
            const currentWeekYear = getYear(referenceSunday);

            console.log(`📅 Current week boundaries: ${format(currentWeekStart, 'yyyy-MM-dd')} to ${format(currentWeekEnd, 'yyyy-MM-dd')}`);
            console.log(`📅 Week ${currentWeekNumber} ${currentWeekYear}`);

            // Generate 12 previous weeks from the reference Sunday
            const weeks = [];
            for (let i = 1; i <= 12; i++) {
              const weekSunday = subWeeks(referenceSunday, i);
              const weekStart = addDays(weekSunday, -6); // Monday before Sunday
              const weekEnd = weekSunday; // Sunday

              weeks.push({
                start: format(weekStart, 'yyyy-MM-dd'),
                end: format(weekEnd, 'yyyy-MM-dd'),
                weekNumber: getWeek(weekSunday, { weekStartsOn: 1 }),
                year: getYear(weekSunday),
                weekIndex: i, // For ordering (1 = most recent, 12 = oldest)
                referenceSunday: format(weekSunday, 'yyyy-MM-dd')
              });
            }

            console.log(`📅 Fetching data for 12 previous weeks (Sunday-based):`);
            weeks.forEach((week, i) => {
              console.log(`  Week ${i + 1}: ${week.start} to ${week.end} (Week ${week.weekNumber} ${week.year})`);
            });

            // Fetch data for all 12 weeks with week metadata
            const allWeeksData = [];
            for (const week of weeks) {
              let weekQuery = supabase
                .from('sales_data_br7x9m4k2q')
                .select('*')
                .gte('txn_date', week.start)
                .lte('txn_date', week.end)
                .order('txn_date', { ascending: true });

              // Apply same filters
              if (departments.length > 0) {
                weekQuery = weekQuery.in('department', departments);
              }
              if (categories.length > 0) {
                weekQuery = weekQuery.in('category', categories);
              }
              if (products.length > 0) {
                weekQuery = weekQuery.in('product_name', products);
              }

              const weekData = await get().fetchAllData(weekQuery);

              // Add week metadata to each record
              const weekDataWithMeta = weekData.map(record => ({
                ...record,
                weekStart: week.start,
                weekEnd: week.end,
                weekNumber: week.weekNumber,
                weekYear: week.year,
                weekIndex: week.weekIndex,
                weekLabel: `Week ${week.weekNumber} ${week.year}`,
                referenceSunday: week.referenceSunday
              }));

              allWeeksData.push(...weekDataWithMeta);
            }

            console.log(`📊 WoW (CUSTOM 12 weeks): Fetched ${allWeeksData.length} records from 12 previous weeks`);

            // Return structured data with week information and current week info
            return {
              data: allWeeksData,
              type: 'custom_12_week_wow',
              weeks: weeks,
              currentWeek: {
                start: format(currentWeekStart, 'yyyy-MM-dd'),
                end: format(currentWeekEnd, 'yyyy-MM-dd'),
                weekNumber: currentWeekNumber,
                year: currentWeekYear,
                referenceSunday: format(referenceSunday, 'yyyy-MM-dd')
              }
            };
          } else {
            // Regular WoW for other presets
            comparisonDateFrom = format(new Date(startDate.getTime() - (7 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd');
            comparisonDateTo = format(new Date(endDate.getTime() - (7 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd');
          }
          break;

        case 'MOM':
          // Month over Month
          comparisonDateFrom = format(new Date(startDate.getTime() - (30 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd');
          comparisonDateTo = format(new Date(endDate.getTime() - (30 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd');
          break;

        default:
          return null;
      }

      // For non-DoW and non-12week-WoW comparisons, use the regular date range approach
      if (compareType !== 'DOW' && !(compareType === 'WOW' && (get().preset === 'WEEK' || get().preset === 'CUSTOM'))) {
        console.log(`📊 Comparison period: ${comparisonDateFrom} to ${comparisonDateTo}`);

        // Build comparison query
        let comparisonQuery = supabase
          .from('sales_data_br7x9m4k2q')
          .select('*')
          .gte('txn_date', comparisonDateFrom)
          .lte('txn_date', comparisonDateTo)
          .order('txn_date', { ascending: true });

        // Apply same filters
        if (departments.length > 0) {
          comparisonQuery = comparisonQuery.in('department', departments);
        }
        if (categories.length > 0) {
          comparisonQuery = comparisonQuery.in('category', categories);
        }
        if (products.length > 0) {
          comparisonQuery = comparisonQuery.in('product_name', products);
        }

        const comparisonData = await get().fetchAllData(comparisonQuery);
        console.log(`📊 Fetched ${comparisonData?.length || 0} comparison records`);
        return comparisonData;
      }
    } catch (error) {
      console.error('Error loading comparison data:', error);
      return null;
    }
  },

  // NEW: Get 7-day comparison data specifically for DATE preset
  getDatePresetComparisonData: async (selectedDate, departments, categories, products) => {
    try {
      const targetDate = new Date(selectedDate);

      // Get the previous 7 days (excluding the selected date)
      const comparisonStartDate = new Date(targetDate);
      comparisonStartDate.setDate(comparisonStartDate.getDate() - 7);
      const comparisonEndDate = new Date(targetDate);
      comparisonEndDate.setDate(comparisonEndDate.getDate() - 1);

      const comparisonDateFrom = format(comparisonStartDate, 'yyyy-MM-dd');
      const comparisonDateTo = format(comparisonEndDate, 'yyyy-MM-dd');

      console.log(`📊 DATE preset 7-day comparison: ${comparisonDateFrom} to ${comparisonDateTo} vs ${selectedDate}`);

      // Build comparison query for the 7-day period
      let comparisonQuery = supabase
        .from('sales_data_br7x9m4k2q')
        .select('*')
        .gte('txn_date', comparisonDateFrom)
        .lte('txn_date', comparisonDateTo)
        .order('txn_date', { ascending: true });

      // Apply same filters
      if (departments.length > 0) {
        comparisonQuery = comparisonQuery.in('department', departments);
      }
      if (categories.length > 0) {
        comparisonQuery = comparisonQuery.in('category', categories);
      }
      if (products.length > 0) {
        comparisonQuery = comparisonQuery.in('product_name', products);
      }

      const comparisonData = await get().fetchAllData(comparisonQuery);
      console.log(`📊 Fetched ${comparisonData?.length || 0} records for 7-day comparison`);
      return comparisonData;
    } catch (error) {
      console.error('Error loading DATE preset comparison data:', error);
      return null;
    }
  },

  // Separate function to reload just ranked data
  loadRankedData: async () => {
    const state = get();
    if (!state.hasData) return;

    try {
      const rankedData = await get().getRankedDataFromDB(
        state.dateFrom,
        state.dateTo,
        state.topBottomMetric,
        state.topBottomMode,
        state.departments,
        state.categories,
        state.products
      );
      set({ rankedData });
    } catch (error) {
      console.error('Error loading ranked data:', error);
    }
  },

  // FIXED: Separate function to reload mini multiples data with pagination
  loadMiniMultipleData: async () => {
    const state = get();
    if (!state.hasData) return;

    try {
      // Build base query for mini multiples
      let baseQuery = supabase
        .from('sales_data_br7x9m4k2q')
        .select('*')
        .gte('txn_date', state.dateFrom)
        .lte('txn_date', state.dateTo)
        .order('txn_date', { ascending: true });

      // Apply filters
      if (state.departments.length > 0) {
        baseQuery = baseQuery.in('department', state.departments);
      }
      if (state.categories.length > 0) {
        baseQuery = baseQuery.in('category', state.categories);
      }
      if (state.products.length > 0) {
        baseQuery = baseQuery.in('product_name', state.products);
      }

      // Fetch ALL data using pagination
      const salesData = await get().fetchAllData(baseQuery);

      if (salesData && salesData.length > 0) {
        const miniMultipleData = get().processMiniMultipleData(salesData);
        set({ miniMultipleData });
      }
    } catch (error) {
      console.error('Error loading mini multiples data:', error);
    }
  },

  // Empty state KPIs
  getEmptyKPIs: () => ({
    revenue: { value: 0, delta: null, deltaPercent: null },
    cost: { value: 0, delta: null, deltaPercent: null },
    profit: { value: 0, delta: null, deltaPercent: null },
    discount: { value: 0, delta: null, deltaPercent: null },
    profitRatio: { value: 0, delta: null, deltaPercent: null }
  }),

  // ENHANCED: Revenue calculation with proper trend comparison including DoW vs DoW, 12-week WoW, and CUSTOM WoW analysis - FIXED to preserve negative values
  processRealDataFixed: async (salesData, dateFrom, dateTo, comparisonData = null) => {
    const state = get();
    console.log(`📊 FIXED Processing ALL ${salesData.length} records from ${dateFrom} to ${dateTo}`);

    // Log a sample of raw data to verify what we're working with
    console.log('🔍 Sample raw data (first 3 records):');
    salesData.slice(0, 3).forEach((row, i) => {
      console.log(`  Record ${i + 1}:`, {
        date: row.txn_date,
        product: row.product_name,
        net_sales: row.net_sales,
        gross_sales: row.gross_sales,
        cogs: row.cogs,
        profit: row.profit,
        discount_amt: row.discount_amt,
        units: row.units
      });
    });

    // FIXED KPI CALCULATIONS - Using precise arithmetic with proper discount handling - PRESERVE NEGATIVE VALUES
    let totalRevenue = 0;
    let totalGrossSales = 0;
    let totalCost = 0;
    let totalProfit = 0;
    let totalDiscount = 0;

    // Process each record individually to avoid floating point errors
    salesData.forEach((row, index) => {
      const netSales = parseFloat(row.net_sales || 0);
      const grossSales = parseFloat(row.gross_sales || 0);
      const cogs = parseFloat(row.cogs || 0);
      const profit = parseFloat(row.profit || 0);
      const discount = parseFloat(row.discount_amt || 0);

      // Accumulate totals with precision - ALLOW NEGATIVE VALUES
      totalRevenue += netSales; // Can be negative (refunds)
      totalGrossSales += grossSales; // Can be negative (refunds)
      totalCost += cogs; // Can be negative (cost adjustments)
      totalProfit += profit; // Can be negative (losses)
      totalDiscount += discount; // Can be negative (surcharges)

      // Log first few calculations for debugging
      if (index < 3) {
        console.log(`  Calculation ${index + 1}:`, {
          netSales,
          grossSales,
          cogs,
          profit,
          discount,
          runningRevenue: totalRevenue,
          runningDiscount: totalDiscount
        });
      }
    });

    // Round to 2 decimal places to handle floating point precision - PRESERVE NEGATIVE VALUES
    totalRevenue = Math.round(totalRevenue * 100) / 100;
    totalGrossSales = Math.round(totalGrossSales * 100) / 100;
    totalCost = Math.round(totalCost * 100) / 100;
    totalProfit = Math.round(totalProfit * 100) / 100;
    totalDiscount = Math.round(totalDiscount * 100) / 100;

    // FIXED: Allow negative profit ratios (which indicate losses)
    const profitRatio = totalRevenue !== 0 ? (totalProfit / totalRevenue) * 100 : 0;

    // FIXED: Ensure discount calculation is working properly with negative values
    console.log(`💰 DISCOUNT CALCULATION DEBUG (including negative values):`);
    console.log(`  Raw discount data from first 5 records:`);
    salesData.slice(0, 5).forEach((row, i) => {
      console.log(`    Record ${i + 1}: discount_amt=${row.discount_amt} (type: ${typeof row.discount_amt})`);
    });
    console.log(`  Total calculated discount: ${totalDiscount}`);
    console.log(`  Total gross sales: ${totalGrossSales}`);
    console.log(`  Total net sales (revenue): ${totalRevenue}`);
    console.log(`  Discount calculation: ${totalGrossSales} - ${totalRevenue} = ${totalGrossSales - totalRevenue}`);

    // Calculate comparison KPIs if comparison data exists
    let comparisonKPIs = null;
    if (comparisonData) {
      console.log(`📊 Processing comparison data...`);

      // Handle structured 12-week WoW data
      const actualComparisonData = comparisonData?.data || comparisonData;

      if (!actualComparisonData || actualComparisonData.length === 0) {
        console.log('📊 No comparison data available');
      } else {
        let compTotalRevenue = 0;
        let compTotalGrossSales = 0;
        let compTotalCost = 0;
        let compTotalProfit = 0;
        let compTotalDiscount = 0;

        // Enhanced comparison calculation for different comparison types
        if (state.compare === 'DOW' && dateFrom === dateTo) {
          console.log(`📊 DoW vs DoW: Calculating averages from 30 similar days of the week...`);
          
          actualComparisonData.forEach((row) => {
            // PRESERVE NEGATIVE VALUES in comparison data
            compTotalRevenue += parseFloat(row.net_sales || 0);
            compTotalGrossSales += parseFloat(row.gross_sales || 0);
            compTotalCost += parseFloat(row.cogs || 0);
            compTotalProfit += parseFloat(row.profit || 0);
            compTotalDiscount += parseFloat(row.discount_amt || 0);
          });

          // Get the number of unique days in the comparison period
          const uniqueDays = [...new Set(actualComparisonData.map(row => row.txn_date))].length;
          console.log(`📊 Found data across ${uniqueDays} days in DoW comparison period`);

          if (uniqueDays > 0) {
            // Calculate daily averages from the DoW comparison - PRESERVE NEGATIVE VALUES
            compTotalRevenue = compTotalRevenue / uniqueDays;
            compTotalGrossSales = compTotalGrossSales / uniqueDays;
            compTotalCost = compTotalCost / uniqueDays;
            compTotalProfit = compTotalProfit / uniqueDays;
            compTotalDiscount = compTotalDiscount / uniqueDays;

            console.log(`📊 Daily averages from ${uniqueDays} similar DoWs: Revenue=${compTotalRevenue.toFixed(2)}, Discount=${compTotalDiscount.toFixed(2)}, Cost=${compTotalCost.toFixed(2)}, Profit=${compTotalProfit.toFixed(2)}`);
          }
        } else if (state.compare === 'WOW' && state.preset === 'WEEK' && (comparisonData?.type === '12_week_wow')) {
          console.log(`📊 WoW (12 weeks): Calculating weekly averages from 12 previous weeks...`);
          
          actualComparisonData.forEach((row) => {
            // PRESERVE NEGATIVE VALUES in comparison data
            compTotalRevenue += parseFloat(row.net_sales || 0);
            compTotalGrossSales += parseFloat(row.gross_sales || 0);
            compTotalCost += parseFloat(row.cogs || 0);
            compTotalProfit += parseFloat(row.profit || 0);
            compTotalDiscount += parseFloat(row.discount_amt || 0);
          });

          // Calculate weekly averages (divide by 12 weeks) - PRESERVE NEGATIVE VALUES
          compTotalRevenue = compTotalRevenue / 12;
          compTotalGrossSales = compTotalGrossSales / 12;
          compTotalCost = compTotalCost / 12;
          compTotalProfit = compTotalProfit / 12;
          compTotalDiscount = compTotalDiscount / 12;

          console.log(`📊 Weekly averages from 12 weeks: Revenue=${compTotalRevenue.toFixed(2)}, Discount=${compTotalDiscount.toFixed(2)}, Cost=${compTotalCost.toFixed(2)}, Profit=${compTotalProfit.toFixed(2)}`);
        } else if (state.compare === 'WOW' && state.preset === 'CUSTOM' && (comparisonData?.type === 'custom_12_week_wow')) {
          console.log(`📊 CUSTOM WoW (12 weeks): Calculating weekly averages from 12 previous Sunday-based weeks...`);
          
          actualComparisonData.forEach((row) => {
            // PRESERVE NEGATIVE VALUES in comparison data
            compTotalRevenue += parseFloat(row.net_sales || 0);
            compTotalGrossSales += parseFloat(row.gross_sales || 0);
            compTotalCost += parseFloat(row.cogs || 0);
            compTotalProfit += parseFloat(row.profit || 0);
            compTotalDiscount += parseFloat(row.discount_amt || 0);
          });

          // Calculate weekly averages (divide by 12 weeks) - PRESERVE NEGATIVE VALUES
          compTotalRevenue = compTotalRevenue / 12;
          compTotalGrossSales = compTotalGrossSales / 12;
          compTotalCost = compTotalCost / 12;
          compTotalProfit = compTotalProfit / 12;
          compTotalDiscount = compTotalDiscount / 12;

          console.log(`📊 CUSTOM Weekly averages from 12 Sunday-based weeks: Revenue=${compTotalRevenue.toFixed(2)}, Discount=${compTotalDiscount.toFixed(2)}, Cost=${compTotalCost.toFixed(2)}, Profit=${compTotalProfit.toFixed(2)}`);
        } else if (state.preset === 'DATE' && dateFrom === dateTo) {
          console.log(`📊 DATE preset: Calculating daily averages from 7-day period...`);
          
          actualComparisonData.forEach((row) => {
            // PRESERVE NEGATIVE VALUES in comparison data
            compTotalRevenue += parseFloat(row.net_sales || 0);
            compTotalGrossSales += parseFloat(row.gross_sales || 0);
            compTotalCost += parseFloat(row.cogs || 0);
            compTotalProfit += parseFloat(row.profit || 0);
            compTotalDiscount += parseFloat(row.discount_amt || 0);
          });

          // Get the number of unique days in the comparison period
          const uniqueDays = [...new Set(actualComparisonData.map(row => row.txn_date))].length;
          console.log(`📊 Found data across ${uniqueDays} days in comparison period`);

          if (uniqueDays > 0) {
            // Calculate daily averages - PRESERVE NEGATIVE VALUES
            compTotalRevenue = compTotalRevenue / uniqueDays;
            compTotalGrossSales = compTotalGrossSales / uniqueDays;
            compTotalCost = compTotalCost / uniqueDays;
            compTotalProfit = compTotalProfit / uniqueDays;
            compTotalDiscount = compTotalDiscount / uniqueDays;

            console.log(`📊 Daily averages from 7-day period: Revenue=${compTotalRevenue.toFixed(2)}, Discount=${compTotalDiscount.toFixed(2)}, Cost=${compTotalCost.toFixed(2)}, Profit=${compTotalProfit.toFixed(2)}`);
          }
        } else {
          // Regular comparison calculation for other presets (WOW, MOM)
          actualComparisonData.forEach((row) => {
            // PRESERVE NEGATIVE VALUES in comparison data
            compTotalRevenue += parseFloat(row.net_sales || 0);
            compTotalGrossSales += parseFloat(row.gross_sales || 0);
            compTotalCost += parseFloat(row.cogs || 0);
            compTotalProfit += parseFloat(row.profit || 0);
            compTotalDiscount += parseFloat(row.discount_amt || 0);
          });
        }

        // Round comparison totals - PRESERVE NEGATIVE VALUES
        compTotalRevenue = Math.round(compTotalRevenue * 100) / 100;
        compTotalGrossSales = Math.round(compTotalGrossSales * 100) / 100;
        compTotalCost = Math.round(compTotalCost * 100) / 100;
        compTotalProfit = Math.round(compTotalProfit * 100) / 100;
        compTotalDiscount = Math.round(compTotalDiscount * 100) / 100;

        // FIXED: Allow negative profit ratios in comparison data
        const compProfitRatio = compTotalRevenue !== 0 ? (compTotalProfit / compTotalRevenue) * 100 : 0;

        comparisonKPIs = {
          revenue: compTotalRevenue,
          cost: compTotalCost,
          profit: compTotalProfit,
          discount: compTotalDiscount,
          profitRatio: compProfitRatio
        };

        console.log('📊 Comparison KPIs:', comparisonKPIs);
      }
    }

    console.log('💰 FIXED KPI Calculations with ALL records (preserving negative values):');
    console.log(`  📊 Total Records Processed: ${salesData.length}`);
    console.log(`  💵 Total Revenue (Net Sales): ${totalRevenue}`);
    console.log(`  💵 Total Gross Sales: ${totalGrossSales}`);
    console.log(`  💰 Total Cost (COGS): ${totalCost}`);
    console.log(`  📈 Total Profit: ${totalProfit}`);
    console.log(`  💸 Total Discount: ${totalDiscount}`);
    console.log(`  📊 Profit Ratio: ${profitRatio.toFixed(2)}%`);

    // Build KPIs with trend calculations - PRESERVE NEGATIVE VALUES
    const kpis = {
      revenue: {
        value: totalRevenue,
        delta: comparisonKPIs ? totalRevenue - comparisonKPIs.revenue : null,
        deltaPercent: comparisonKPIs && comparisonKPIs.revenue !== 0 ? ((totalRevenue - comparisonKPIs.revenue) / Math.abs(comparisonKPIs.revenue)) * 100 : null // Use abs for percentage calc but preserve sign
      },
      cost: {
        value: totalCost,
        delta: comparisonKPIs ? totalCost - comparisonKPIs.cost : null,
        deltaPercent: comparisonKPIs && comparisonKPIs.cost !== 0 ? ((totalCost - comparisonKPIs.cost) / Math.abs(comparisonKPIs.cost)) * 100 : null
      },
      profit: {
        value: totalProfit,
        delta: comparisonKPIs ? totalProfit - comparisonKPIs.profit : null,
        deltaPercent: comparisonKPIs && comparisonKPIs.profit !== 0 ? ((totalProfit - comparisonKPIs.profit) / Math.abs(comparisonKPIs.profit)) * 100 : null
      },
      discount: {
        value: totalDiscount,
        delta: comparisonKPIs ? totalDiscount - comparisonKPIs.discount : null,
        deltaPercent: comparisonKPIs && comparisonKPIs.discount !== 0 ? ((totalDiscount - comparisonKPIs.discount) / Math.abs(comparisonKPIs.discount)) * 100 : null
      },
      profitRatio: {
        value: profitRatio,
        delta: comparisonKPIs ? profitRatio - comparisonKPIs.profitRatio : null,
        deltaPercent: comparisonKPIs && comparisonKPIs.profitRatio !== 0 ? ((profitRatio - comparisonKPIs.profitRatio) / Math.abs(comparisonKPIs.profitRatio)) * 100 : null
      }
    };

    // Log trend calculations if available
    if (comparisonKPIs) {
      let comparisonType = 'comparison period';
      if (state.compare === 'DOW' && dateFrom === dateTo) {
        const selectedDate = new Date(dateFrom);
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayOfWeek = getDay(selectedDate);
        comparisonType = `30 ${dayNames[dayOfWeek]} average`;
      } else if (state.compare === 'WOW' && state.preset === 'WEEK') {
        comparisonType = '12-week average';
      } else if (state.compare === 'WOW' && state.preset === 'CUSTOM') {
        comparisonType = '12 Sunday-week average';
      } else if (state.preset === 'DATE' && dateFrom === dateTo) {
        comparisonType = '7-day average';
      }

      console.log(`📈 TREND CALCULATIONS (vs ${comparisonType}) - preserving negative values:`);
      Object.keys(kpis).forEach(key => {
        const kpi = kpis[key];
        if (kpi.delta !== null) {
          console.log(`  ${key}: ${kpi.value} vs ${comparisonKPIs[key === 'profitRatio' ? 'profitRatio' : key]} = ${kpi.delta.toFixed(2)} (${kpi.deltaPercent?.toFixed(1)}%)`);
        }
      });
    }

    // Create time series with intelligent date handling
    console.log('📈 Creating smart time series with ALL data');

    // Group sales data by date with FIXED calculations - PRESERVE NEGATIVE VALUES
    const dateGroups = salesData.reduce((acc, row) => {
      const date = row.txn_date;
      if (!acc[date]) {
        acc[date] = { revenue: 0, profit: 0, units: 0 };
      }

      // PRESERVE NEGATIVE VALUES in daily aggregations
      acc[date].revenue += parseFloat(row.net_sales || 0);
      acc[date].profit += parseFloat(row.profit || 0);
      acc[date].units += parseInt(row.units || 0);
      return acc;
    }, {});

    // Round daily totals for precision - PRESERVE NEGATIVE VALUES
    Object.keys(dateGroups).forEach(date => {
      dateGroups[date].revenue = Math.round(dateGroups[date].revenue * 100) / 100;
      dateGroups[date].profit = Math.round(dateGroups[date].profit * 100) / 100;
    });

    // Enhanced series data creation for different comparison modes
    let seriesToShow = [];

    if (state.compare === 'DOW' && dateFrom === dateTo && comparisonData) {
      console.log('📊 DoW vs DoW: Creating enhanced series with 30-day DoW context...');

      // Group comparison data by date - PRESERVE NEGATIVE VALUES
      const actualComparisonData = comparisonData?.data || comparisonData;
      const comparisonDateGroups = actualComparisonData.reduce((acc, row) => {
        const date = row.txn_date;
        if (!acc[date]) {
          acc[date] = { revenue: 0, profit: 0, units: 0 };
        }

        // PRESERVE NEGATIVE VALUES
        acc[date].revenue += parseFloat(row.net_sales || 0);
        acc[date].profit += parseFloat(row.profit || 0);
        acc[date].units += parseInt(row.units || 0);
        return acc;
      }, {});

      // Get all comparison dates and sort them
      const comparisonDates = Object.keys(comparisonDateGroups).sort();

      // Create series with comparison days + selected date
      const allDates = [...comparisonDates, dateFrom].sort();

      seriesToShow = allDates.map(date => {
        const isSelectedDate = date === dateFrom;
        const dataSource = isSelectedDate ? dateGroups : comparisonDateGroups;
        const dayData = dataSource[date] || { revenue: 0, profit: 0, units: 0 };

        return {
          date,
          revenue: dayData.revenue, // Can be negative
          profit: dayData.profit, // Can be negative
          units: dayData.units, // Can be negative
          isSelected: isSelectedDate,
          isComparison: !isSelectedDate
        };
      });

      console.log(`📊 Generated ${seriesToShow.length} data points (${comparisonDates.length} DoW comparison + 1 selected) for DoW vs DoW`);
    } else if (state.compare === 'WOW' && (state.preset === 'WEEK' || state.preset === 'CUSTOM') && (comparisonData?.type === '12_week_wow' || comparisonData?.type === 'custom_12_week_wow')) {
      console.log(`📊 WoW (13 weeks): Creating enhanced series with ALL 13 weeks for ${state.preset} preset...`);

      // Group comparison data by week - PRESERVE NEGATIVE VALUES
      const actualComparisonData = comparisonData.data;
      const weekGroups = {};

      // Group data by week
      actualComparisonData.forEach(row => {
        const weekKey = `${row.weekYear}-W${String(row.weekNumber).padStart(2, '0')}`;
        if (!weekGroups[weekKey]) {
          weekGroups[weekKey] = {
            revenue: 0,
            profit: 0,
            units: 0,
            weekStart: row.weekStart,
            weekEnd: row.weekEnd,
            weekNumber: row.weekNumber,
            weekYear: row.weekYear,
            weekIndex: row.weekIndex,
            weekLabel: row.weekLabel
          };
        }

        // PRESERVE NEGATIVE VALUES
        weekGroups[weekKey].revenue += parseFloat(row.net_sales || 0);
        weekGroups[weekKey].profit += parseFloat(row.profit || 0);
        weekGroups[weekKey].units += parseInt(row.units || 0);
      });

      // Round weekly totals - PRESERVE NEGATIVE VALUES
      Object.keys(weekGroups).forEach(weekKey => {
        weekGroups[weekKey].revenue = Math.round(weekGroups[weekKey].revenue * 100) / 100;
        weekGroups[weekKey].profit = Math.round(weekGroups[weekKey].profit * 100) / 100;
      });

      // Create current week data
      let currentWeekStart, currentWeekEnd, currentWeekNumber, currentWeekYear, currentWeekKey;

      if (comparisonData.type === 'custom_12_week_wow') {
        // Use the calculated current week from CUSTOM preset
        const currentWeek = comparisonData.currentWeek;
        currentWeekStart = currentWeek.start;
        currentWeekEnd = currentWeek.end;
        currentWeekNumber = currentWeek.weekNumber;
        currentWeekYear = currentWeek.year;
        console.log(`📊 CUSTOM preset current week: ${currentWeekStart} to ${currentWeekEnd} (Week ${currentWeekNumber} ${currentWeekYear})`);
      } else {
        // Use Monday-Sunday week for WEEK preset
        currentWeekStart = format(startOfWeek(new Date(dateFrom), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        currentWeekEnd = format(endOfWeek(new Date(dateFrom), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        currentWeekNumber = getWeek(new Date(dateFrom), { weekStartsOn: 1 });
        currentWeekYear = getYear(new Date(dateFrom));
        console.log(`📊 WEEK preset current week: ${currentWeekStart} to ${currentWeekEnd} (Week ${currentWeekNumber} ${currentWeekYear})`);
      }

      currentWeekKey = `${currentWeekYear}-W${String(currentWeekNumber).padStart(2, '0')}`;

      weekGroups[currentWeekKey] = {
        revenue: totalRevenue, // Can be negative
        profit: totalProfit, // Can be negative
        units: salesData.reduce((sum, row) => sum + parseInt(row.units || 0), 0), // Can be negative
        weekStart: currentWeekStart,
        weekEnd: currentWeekEnd,
        weekNumber: currentWeekNumber,
        weekYear: currentWeekYear,
        weekIndex: 0, // Current week
        weekLabel: `Week ${currentWeekNumber} ${currentWeekYear}`,
        isCurrent: true
      };

      // CRITICAL FIX: Convert to array and sort chronologically to show ALL 13 weeks
      const sortedWeeks = Object.entries(weekGroups)
        .map(([key, data]) => ({
          date: data.weekStart, // Use week start as the date key
          ...data,
          weekKey: key
        }))
        .sort((a, b) => {
          // FIXED: Sort by actual date to show chronological order from oldest to newest
          const dateA = new Date(a.weekStart);
          const dateB = new Date(b.weekStart);
          return dateA - dateB; // Oldest first, newest (current) last
        });

      seriesToShow = sortedWeeks.map(week => ({
        date: week.weekStart,
        revenue: week.revenue, // Can be negative
        profit: week.profit, // Can be negative
        units: week.units, // Can be negative
        weekLabel: week.weekLabel,
        weekNumber: week.weekNumber,
        weekYear: week.weekYear,
        isCurrent: week.isCurrent || false,
        isComparison: !week.isCurrent
      }));

      console.log(`📊 FIXED: Generated ${seriesToShow.length} data points (ALL 13 weeks: 12 comparison + 1 current) for WoW analysis`);
      console.log('📊 Complete week breakdown (chronological order - oldest to newest):');
      seriesToShow.forEach((week, i) => {
        console.log(`  ${i + 1}. ${week.weekLabel}: ${week.revenue.toFixed(2)} revenue ${week.isCurrent ? '(CURRENT)' : '(comparison)'} [${week.date}]`);
      });
    } else if (state.preset === 'DATE' && dateFrom === dateTo && comparisonData) {
      console.log('📊 DATE preset: Creating enhanced series with 7-day context...');

      // Group comparison data by date - PRESERVE NEGATIVE VALUES
      const actualComparisonData = comparisonData?.data || comparisonData;
      const comparisonDateGroups = actualComparisonData.reduce((acc, row) => {
        const date = row.txn_date;
        if (!acc[date]) {
          acc[date] = { revenue: 0, profit: 0, units: 0 };
        }

        // PRESERVE NEGATIVE VALUES
        acc[date].revenue += parseFloat(row.net_sales || 0);
        acc[date].profit += parseFloat(row.profit || 0);
        acc[date].units += parseInt(row.units || 0);
        return acc;
      }, {});

      // Create series with 7 comparison days + selected date
      const targetDate = new Date(dateFrom);
      const allDates = [];

      // Add 7 previous days
      for (let i = 7; i >= 1; i--) {
        const date = new Date(targetDate);
        date.setDate(date.getDate() - i);
        allDates.push(format(date, 'yyyy-MM-dd'));
      }

      // Add selected date
      allDates.push(dateFrom);

      seriesToShow = allDates.map(date => {
        const isSelectedDate = date === dateFrom;
        const dataSource = isSelectedDate ? dateGroups : comparisonDateGroups;
        const dayData = dataSource[date] || { revenue: 0, profit: 0, units: 0 };

        return {
          date,
          revenue: dayData.revenue, // Can be negative
          profit: dayData.profit, // Can be negative
          units: dayData.units, // Can be negative
          isSelected: isSelectedDate
        };
      });

      console.log(`📊 Generated ${seriesToShow.length} data points (7 comparison + 1 selected) for DATE preset`);
    } else {
      // Regular series data for other presets
      const datesWithData = Object.keys(dateGroups).sort();
      console.log(`📅 Found data for ${datesWithData.length} unique dates`);

      if (datesWithData.length > 0) {
        const requestedDateRange = eachDayOfInterval({
          start: parseISO(dateFrom),
          end: parseISO(dateTo)
        }).length;

        const dataDateCoverage = datesWithData.length / requestedDateRange;

        if (dataDateCoverage < 0.3) {
          // Less than 30% coverage, show only dates with data
          console.log(`📊 Low data coverage (${(dataDateCoverage * 100).toFixed(1)}%), showing only dates with data`);
          seriesToShow = datesWithData.map(date => ({
            date,
            revenue: dateGroups[date].revenue, // Can be negative
            profit: dateGroups[date].profit, // Can be negative
            units: dateGroups[date].units // Can be negative
          }));
        } else {
          console.log(`📊 Good data coverage (${(dataDateCoverage * 100).toFixed(1)}%), showing complete range`);
          const allDatesInRange = eachDayOfInterval({
            start: parseISO(dateFrom),
            end: parseISO(dateTo)
          });

          seriesToShow = allDatesInRange.map(date => {
            const dateString = format(date, 'yyyy-MM-dd');
            const existingData = dateGroups[dateString];

            return {
              date: dateString,
              revenue: existingData ? existingData.revenue : 0, // Can be negative
              profit: existingData ? existingData.profit : 0, // Can be negative
              units: existingData ? existingData.units : 0 // Can be negative
            };
          });
        }
      }
    }

    console.log(`📊 Final series data contains ${seriesToShow.length} data points for trend chart`);

    // Get ranked data
    const rankedData = await get().getRankedDataFromDB(
      dateFrom,
      dateTo,
      state.topBottomMetric,
      state.topBottomMode,
      state.departments,
      state.categories,
      state.products
    );

    // Mini multiples data
    const miniMultipleData = get().processMiniMultipleData(salesData);

    return {
      kpis,
      seriesData: seriesToShow,
      rankedData,
      miniMultipleData
    };
  },

  // UPDATED: Get ranked data from database with pagination and EXCLUDE zero net revenue products
  getRankedDataFromDB: async (dateFrom, dateTo, metric, mode, departments, categories, products) => {
    try {
      console.log(`📊 Loading ranked data for metric: ${metric}, mode: ${mode} with ALL records`);
      console.log(`🚫 EXCLUSION RULE: Products with Net Revenue = 0 will be EXCLUDED from rankings`);

      // Build base query for ranked data
      let baseQuery = supabase
        .from('sales_data_br7x9m4k2q')
        .select('product_name, net_sales, units, profit')
        .gte('txn_date', dateFrom)
        .lte('txn_date', dateTo);

      // Apply filters
      if (departments.length > 0) {
        baseQuery = baseQuery.in('department', departments);
      }
      if (categories.length > 0) {
        baseQuery = baseQuery.in('category', categories);
      }
      if (products.length > 0) {
        baseQuery = baseQuery.in('product_name', products);
      }

      // Fetch ALL data using pagination
      const rawData = await get().fetchAllData(baseQuery);

      if (!rawData || rawData.length === 0) {
        return [];
      }

      console.log(`📊 Processing ${rawData.length} raw records for product aggregation...`);

      // Aggregate by product with FIXED calculations - PRESERVE NEGATIVE VALUES
      const productTotals = rawData.reduce((acc, row) => {
        const product = row.product_name;
        if (!acc[product]) {
          acc[product] = {
            total_revenue: 0,
            total_units: 0,
            total_profit: 0
          };
        }

        // PRESERVE NEGATIVE VALUES in aggregations
        acc[product].total_revenue += parseFloat(row.net_sales || 0);
        acc[product].total_units += parseInt(row.units || 0);
        acc[product].total_profit += parseFloat(row.profit || 0);
        return acc;
      }, {});

      // Round product totals for precision - PRESERVE NEGATIVE VALUES
      Object.keys(productTotals).forEach(product => {
        productTotals[product].total_revenue = Math.round(productTotals[product].total_revenue * 100) / 100;
        productTotals[product].total_profit = Math.round(productTotals[product].total_profit * 100) / 100;
      });

      console.log(`📊 Aggregated into ${Object.keys(productTotals).length} unique products`);

      // Convert to array and apply EXCLUSION RULE
      let aggregatedData = Object.entries(productTotals)
        .map(([product_name, totals]) => ({
          product_name,
          ...totals
        }));

      // UPDATED: Apply exclusion rule - EXCLUDE products where Net Revenue = 0 (regardless of units)
      const beforeExclusionCount = aggregatedData.length;
      
      aggregatedData = aggregatedData.filter(item => {
        const isExcluded = (item.total_revenue === 0); // Only exclude if Net Revenue is exactly 0
        
        if (isExcluded) {
          console.log(`🚫 EXCLUDED: ${item.product_name} (Net Revenue: ${item.total_revenue})`);
        }
        
        return !isExcluded; // Keep products that are NOT excluded
      });

      const afterExclusionCount = aggregatedData.length;
      const excludedCount = beforeExclusionCount - afterExclusionCount;

      console.log(`📊 EXCLUSION RESULTS:`);
      console.log(`  📦 Products before exclusion: ${beforeExclusionCount}`);
      console.log(`  🚫 Products excluded (Net Revenue = 0): ${excludedCount}`);
      console.log(`  ✅ Products remaining for ranking: ${afterExclusionCount}`);

      if (aggregatedData.length === 0) {
        console.log('📊 No products remaining after exclusion rule');
        return [];
      }

      // Sort by the selected metric (always ascending first)
      aggregatedData.sort((a, b) => {
        switch (metric) {
          case 'units':
            return a.total_units - b.total_units; // Ascending (including negatives)
          case 'profit':
            return a.total_profit - b.total_profit; // Ascending (including negatives)
          case 'revenue':
          default:
            return a.total_revenue - b.total_revenue; // Ascending (including negatives)
        }
      });

      // For TOP mode: take the last 10 (highest values) and reverse to show highest first
      // For BOTTOM mode: take the first 10 (lowest values) and keep in ascending order
      if (mode === 'TOP') {
        aggregatedData = aggregatedData.slice(-10).reverse();
        console.log(`📊 TOP 10 mode: Taking last 10 and reversing (highest to lowest, preserving negative values, excluding zero net revenue)`);
      } else {
        aggregatedData = aggregatedData.slice(0, 10);
        console.log(`📊 BOTTOM 10 mode: Taking first 10 (lowest to highest, including negative values, excluding zero net revenue)`);
      }

      console.log(`📊 Final ranked list contains ${aggregatedData.length} products`);

      return get().formatRankedData(aggregatedData, metric);
    } catch (error) {
      console.error('Error getting ranked data from DB:', error);
      return [];
    }
  },

  // Helper function to format ranked data - PRESERVE NEGATIVE VALUES
  formatRankedData: (aggregatedData, metric) => {
    if (!aggregatedData || aggregatedData.length === 0) {
      return [];
    }

    // Calculate total for percentage calculation based on selected metric - HANDLE NEGATIVE VALUES
    const totalForMetric = aggregatedData.reduce((sum, row) => {
      switch (metric) {
        case 'units':
          return sum + (row.total_units || 0); // Can be negative
        case 'profit':
          return sum + (row.total_profit || 0); // Can be negative
        case 'revenue':
        default:
          return sum + (row.total_revenue || 0); // Can be negative
      }
    }, 0);

    // Format the data for display
    const rankedData = aggregatedData.map((row, index) => {
      let value, percent;

      // Set the value based on the selected metric
      switch (metric) {
        case 'units':
          value = row.total_units || 0; // Can be negative
          // FIXED: Handle percentage calculation with negative totals
          percent = totalForMetric !== 0 ? (value / totalForMetric) * 100 : 0;
          break;
        case 'profit':
          value = row.total_profit || 0; // Can be negative
          // FIXED: Handle percentage calculation with negative totals
          percent = totalForMetric !== 0 ? (value / totalForMetric) * 100 : 0;
          break;
        case 'revenue':
        default:
          value = row.total_revenue || 0; // Can be negative
          // FIXED: Handle percentage calculation with negative totals
          percent = totalForMetric !== 0 ? (value / totalForMetric) * 100 : 0;
          break;
      }

      return {
        name: row.product_name,
        value: value, // This is the primary value for the selected metric (can be negative)
        revenue: row.total_revenue || 0, // Always include revenue for context (can be negative)
        units: row.total_units || 0, // Always include units for context (can be negative)
        profit: row.total_profit || 0, // Always include profit for context (can be negative)
        percent: percent // Can be negative if individual value is negative or if total is negative
      };
    });

    console.log(`📊 Formatted ranked data (preserving negative values, excluding zero net revenue):`, rankedData.slice(0, 3)); // Log first 3 for debugging
    return rankedData;
  },

  // UPDATED: Process mini multiples data with complete date range bars - PRESERVE NEGATIVE VALUES
  processMiniMultipleData: (salesData) => {
    const state = get();
    const { dateFrom, dateTo, miniMultipleGroup } = state;

    console.log(`🔄 Processing mini multiples data by: ${miniMultipleGroup} with ALL ${salesData.length} records`);
    console.log(`📅 Date range: ${dateFrom} to ${dateTo}`);

    // Generate complete date range for the selected period
    const allDatesInRange = eachDayOfInterval({
      start: parseISO(dateFrom),
      end: parseISO(dateTo)
    });

    console.log(`📊 Complete date range has ${allDatesInRange.length} days`);

    // Define excluded categories/departments
    const excludedItems = ['Condiments', 'Packaging'];

    // Group data by department or category and then by date - PRESERVE NEGATIVE VALUES
    const groups = salesData.reduce((acc, row) => {
      const groupKey = row[miniMultipleGroup] || 'Unknown';

      // Skip excluded categories/departments
      if (excludedItems.includes(groupKey)) {
        return acc;
      }

      if (!acc[groupKey]) {
        acc[groupKey] = {};
      }

      const date = row.txn_date;
      if (!acc[groupKey][date]) {
        acc[groupKey][date] = 0;
      }

      // PRESERVE NEGATIVE VALUES in mini multiples
      acc[groupKey][date] += parseFloat(row.net_sales || 0);
      return acc;
    }, {});

    console.log(`📊 Found ${Object.keys(groups).length} unique ${miniMultipleGroup} groups:`, Object.keys(groups));

    // Convert to mini chart format with complete date range - PRESERVE NEGATIVE VALUES
    const miniData = Object.entries(groups)
      .map(([name, dateData]) => {
        // Create chart data for ALL dates in the selected range
        const chartData = allDatesInRange.map((date, index) => {
          const dateString = format(date, 'yyyy-MM-dd');
          const value = dateData[dateString] || 0; // 0 if no sales on that date (can be negative)

          return {
            date: dateString,
            day: index + 1, // Sequential day number for X-axis
            value: Math.round(value * 100) / 100 // Round for precision, preserve negative
          };
        });

        // Calculate total revenue for this group - PRESERVE NEGATIVE VALUES
        const total = Math.round(
          Object.values(dateData).reduce((sum, val) => sum + val, 0) * 100
        ) / 100;

        console.log(`📊 ${name}: ${chartData.length} bars, total: ${total}`);

        return {
          name,
          data: chartData,
          total: total // Can be negative
        };
      })
      // REMOVED: .filter(group => group.total > 0) // Keep groups with negative totals too
      // Sort by total revenue descending (highest first, then negative values)
      .sort((a, b) => b.total - a.total);

    console.log(`📊 Mini multiples summary (including negative values):`);
    miniData.forEach(group => {
      console.log(`  ${group.name}: ${group.data.length} bars, ${group.total} total revenue`);
    });

    return miniData.length > 0 ? miniData : null;
  },

  // FIXED: Get all data for drill drawer with pagination
  getDrillData: async () => {
    const state = get();
    const { dateFrom, dateTo, departments, categories, products } = state;

    // Build base query for drill data
    let baseQuery = supabase
      .from('sales_data_br7x9m4k2q')
      .select('*')
      .gte('txn_date', dateFrom)
      .lte('txn_date', dateTo)
      .order('txn_date', { ascending: false });

    if (departments.length > 0) {
      baseQuery = baseQuery.in('department', departments);
    }
    if (categories.length > 0) {
      baseQuery = baseQuery.in('category', categories);
    }
    if (products.length > 0) {
      baseQuery = baseQuery.in('product_name', products);
    }

    // Fetch ALL data using pagination
    const allData = await get().fetchAllData(baseQuery);
    return allData || [];
  },

  // UPDATED: Initialize store with latest date initialization
  init: async () => {
    console.log('🚀 Initializing dashboard store...');
    await get().initializeWithLatestDate();
  }
}));

export default useDashboardStore;